import React, { useEffect } from "react";
import { Outlet } from "react-router-dom";
import TopBar from "./TopBar";
import RequirePhoneDialog from "./RequirePhoneDialog";
import { useAuth } from "../contexts/AuthContext";
import { useNewOrderListener } from "../hooks/useNewOrderListener";
import { useOverdueOrderListener } from "../hooks/useOverdueOrderListener";
import { enablePush, getPushStatus } from "../lib/push";

/**
 * Persistent layout for all authenticated app routes (client, admin,
 * super-admin). Renders the TopBar exactly once and an <Outlet /> for the
 * matched child route. Hoisting the TopBar above the Routes ensures the
 * brand logo, company picker and nav stay mounted across in-app navigation,
 * eliminating the per-page re-mount flash that used to "blink" the logo.
 *
 * Also hosts the company-admin "new order" listener so a freshly received
 * order surfaces a toast/notification regardless of which admin sub-page
 * the user is currently on.
 */
export default function AppShell() {
  const { user } = useAuth();
  const isAdmin = user?.role === "company_admin";
  const isClient = user?.role === "client";

  // Ask once for browser-notification permission on the admin shell so we
  // can fire system notifications when the tab is hidden. Soft delay to
  // avoid the prompt feeling intrusive right at login.
  useEffect(() => {
    if (!isAdmin) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "default") return;
    const t = setTimeout(() => {
      try { Notification.requestPermission().catch(() => {}); } catch (_e) { /* ignore */ }
    }, 2000);
    return () => clearTimeout(t);
  }, [isAdmin]);

  // Auto-activación de push para CLIENTES: por default queremos que las
  // notificaciones de pedido lleguen sin que el cliente tenga que ir al
  // perfil. Intentamos suscribir una vez por usuario/dispositivo; si el
  // navegador ya tiene permiso "denied" o el usuario ya fue preguntado,
  // no insistimos. Una flag en localStorage evita re-pedir si el cliente
  // se desuscribe a propósito desde Perfil.
  useEffect(() => {
    if (!isClient || !user?.user_id) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    const flagKey = `push_auto_attempted_${user.user_id}`;
    if (localStorage.getItem(flagKey)) return;
    const t = setTimeout(async () => {
      try {
        const status = await getPushStatus();
        if (!status.supported) return;
        if (status.permission !== "default" || status.subscribed) return;
        // Marca antes de pedir para que no reintente si el usuario rechaza.
        localStorage.setItem(flagKey, "1");
        await enablePush();
      } catch (_e) { /* silencioso */ }
    }, 3000);
    return () => clearTimeout(t);
  }, [isClient, user?.user_id]);

  useNewOrderListener({ enabled: isAdmin });
  useOverdueOrderListener({ enabled: isAdmin });

  return (
    <div className="min-h-screen bg-neutral-50">
      <TopBar />
      <Outlet />
      <RequirePhoneDialog />
    </div>
  );
}
